import { Decimal } from 'decimal.js';

/**
 * 金額の型は NUMERIC（02 §11 #8）。浮動小数点を使わない。
 * pg は NUMERIC を文字列で返すので、アプリ内でも文字列（10 進表記）で持ち回り、
 * 計算するときだけ Decimal に変換する。
 */
export type Money = string;

Decimal.set({ precision: 30, rounding: Decimal.ROUND_HALF_UP });

export function money(value: string | number | Decimal): Money {
  return new Decimal(value).toFixed();
}

export function addMoney(...values: Array<Money | number>): Money {
  return values.reduce<Decimal>((acc, v) => acc.plus(v), new Decimal(0)).toFixed();
}

export function subMoney(a: Money | number, b: Money | number): Money {
  return new Decimal(a).minus(b).toFixed();
}

export function mulMoney(a: Money | number, b: Money | number): Money {
  return new Decimal(a).times(b).toFixed();
}

export function compareMoney(a: Money | number, b: Money | number): -1 | 0 | 1 {
  return new Decimal(a).comparedTo(b) as -1 | 0 | 1;
}

export function isValidMoney(value: unknown): value is Money {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  try {
    const d = new Decimal(value);
    return d.isFinite();
  } catch {
    return false;
  }
}

/**
 * 消費税額（円未満切り捨て）。税率は 0.1 / 0.08 / 0 のような小数で渡す。
 * 内税（税込金額から税額を求める）: tax = amount - amount / (1 + rate)
 */
export function taxIncludedAmountToTax(taxIncluded: Money | number, rate: Money | number): Money {
  const total = new Decimal(taxIncluded);
  const r = new Decimal(rate);
  if (r.isZero()) return '0';
  const net = total.div(r.plus(1));
  return total.minus(net).toDecimalPlaces(0, Decimal.ROUND_FLOOR).toFixed();
}

/** 3 桁区切り・¥ 前置・半角（05 §3.3c）。 */
export function formatJpy(value: Money | number): string {
  const d = new Decimal(value);
  const neg = d.isNegative();
  const [intPart, frac] = d.abs().toFixed().split('.');
  const grouped = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}¥${grouped}${frac ? '.' + frac : ''}`;
}
